import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GraduationCap, Hourglass, CheckCircle2, Video, Crown, Coins } from 'lucide-react';
import api from '../../lib/api';
import { goPro } from '../../lib/checkout';
import { fill, usePageCopy } from '../../lib/pageCopy';
import { FeedbackRequestModal } from './FeedbackRequestModal';
import { PackPicker } from './PackPicker';

const when = (iso) => (iso ? new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '');

/** Results page card — the single entry point for teacher feedback on this attempt.
 *  Apply → "Submitted for review" (waiting) → "Reviewed by teacher" (watch the video). `?feedback=1` (nudge e-mail) opens the request popup. */
export function FeedbackPrompt({ attemptId, isPro }) {
  const c = usePageCopy('teacher_feedback');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null); // { scope }
  const [buying, setBuying] = useState(false);
  const load = useCallback(() => api.get(`/api/feedback-credits/attempt/${attemptId}`).then((r) => setData(r.data)).catch(() => setData({ options: [], requests: [], balance: 0, packs: [] })), [attemptId]);
  useEffect(() => { if (isPro) load(); }, [isPro, load]);
  useEffect(() => { if (isPro && params.get('feedback') === '1') { setModal({ scope: '' }); params.delete('feedback'); setParams(params, { replace: true }); } }, [isPro, params, setParams]);

  if (!isPro) {
    return (
      <div className="mx-auto mb-4 max-w-xl rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 text-left flex items-start gap-3" data-testid="feedback-prompt-free">
        <div className="w-9 h-9 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0"><Crown size={18} /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900">{c.results_title}</p>
          <p className="text-xs text-slate-600 mt-0.5">{c.results_free}</p>
          <button type="button" onClick={() => goPro(navigate)} className="mt-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-4 py-1.5" data-testid="feedback-prompt-upgrade">Upgrade to Pro</button>
        </div>
      </div>
    );
  }
  if (!data) return null;
  const open = data.requests.filter((r) => r.status === 'pending' || r.status === 'in_review');
  const delivered = data.requests.filter((r) => r.status === 'delivered');
  const avail = data.options.filter((o) => o.available);
  const first = avail[0];
  const short = first && data.balance < first.cost;
  const more = avail.length > 0 && (open.length > 0 || delivered.length > 0);
  const modalEl = modal && <FeedbackRequestModal attemptId={attemptId} initialScope={modal.scope} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />;
  const another = more && (
    <button type="button" onClick={() => setModal({ scope: first.scope })} className="mt-2 text-xs font-semibold text-teal-800 hover:underline" data-testid="feedback-prompt-another">
      + Ask for another section ({avail.map((o) => o.label).join(', ')})
    </button>
  );

  if (delivered.length > 0 || open.length > 0) {
    return (
      <div className="mx-auto mb-4 max-w-xl space-y-2 text-left" data-testid="feedback-prompt-status">
        {delivered.map((r) => (
          <div key={r.id} className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 px-4 py-3 flex items-start gap-3" data-testid={`feedback-delivered-${r.id}`}>
            <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0"><CheckCircle2 size={18} /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900">Reviewed by teacher ✅ · {r.scope_label}</p>
              <p className="text-xs text-slate-600 mt-0.5">{fill(c.results_delivered, { label: r.scope_label, teacher: r.teacher_name || 'Your teacher' })} <span className="text-slate-400">· {when(r.delivered_at)}</span></p>
              {r.note && <p className="mt-2 text-sm text-slate-800 whitespace-pre-wrap bg-white/70 rounded-lg px-3 py-2" data-testid={`feedback-delivered-note-${r.id}`}>{r.note}</p>}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {r.loom_url && <a href={r.loom_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 text-white text-xs font-bold px-4 py-1.5 hover:bg-emerald-800" data-testid={`feedback-watch-${r.id}`}><Video size={13} /> Watch your video feedback</a>}
                <button type="button" onClick={() => document.getElementById('teacher-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs font-semibold text-emerald-800 hover:underline" data-testid={`feedback-remarks-${r.id}`}>See remarks on my answers ↓</button>
              </div>
            </div>
          </div>
        ))}
        {open.map((r) => (
          <div key={r.id} className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 px-4 py-3 flex items-start gap-3" data-testid={`feedback-waiting-${r.id}`}>
            <div className="w-9 h-9 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0"><Hourglass size={18} /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900">Submitted for review ⏳ · {r.scope_label}</p>
              <p className="text-xs text-slate-600 mt-0.5">{fill(c.results_waiting, { label: r.scope_label })}</p>
              <p className="text-[11px] text-slate-500 mt-1">Requested {when(r.created_at)} · {r.cost} credits{r.teacher_name ? ` · ${r.teacher_name} is reviewing` : ''}</p>
            </div>
          </div>
        ))}
        {another}
        {modalEl}
      </div>
    );
  }
  if (!first) return null;
  return (
    <div className="mx-auto mb-4 max-w-xl rounded-2xl border border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50 px-4 py-3 text-left flex items-start gap-3" data-testid="feedback-prompt">
      <div className="w-9 h-9 rounded-xl bg-teal-700 text-white flex items-center justify-center shrink-0"><GraduationCap size={18} /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-900">{c.results_title}</p>
        <p className="text-xs text-slate-700 mt-0.5 font-semibold" data-testid="feedback-prompt-balance">{fill(c.results_body, { balance: data.balance, label: first.label, cost: first.cost })}{short ? ` You need ${first.cost - data.balance} more.` : ''}</p>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">{c.results_pitch}</p>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {short ? (
            <button type="button" onClick={() => setBuying(true)} className="inline-flex items-center gap-1.5 rounded-full bg-teal-700 text-white text-xs font-bold px-4 py-1.5 hover:bg-teal-800" data-testid="feedback-prompt-buy"><Coins size={13} /> Buy credits</button>
          ) : (
            <button type="button" onClick={() => setModal({ scope: first.scope })} className="rounded-full bg-teal-700 text-white text-xs font-bold px-4 py-1.5 hover:bg-teal-800" data-testid="feedback-prompt-yes">Yes — review my {first.label}</button>
          )}
          {avail.length > 1 && <button type="button" onClick={() => setModal({ scope: first.scope })} className="text-xs font-semibold text-teal-800 hover:underline" data-testid="feedback-prompt-choose">Choose a different section</button>}
        </div>
      </div>
      {modalEl}
      {buying && <PackPicker packs={data.packs} currency="cad" onClose={() => setBuying(false)} />}
    </div>
  );
}
